import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import FormStepper from '../../../components/common/FormStepper/FormStepper';
import Loader from '../../../components/common/Loader/Loader';
import {
  REACT_APP_URL_ROLE_LIST_ALL,
  REACT_APP_URL_USER_ID,
  REACT_APP_URL_USER_SAVE,
  REACT_APP_URL_USER_UPDATE
} from '../../../components/common/Routes/URLs';
import { resetEntireState } from '../../../Redux/actions/customPayrollActions';
import TempEdgeApi from '../../../services/TempEdgeApi';
import { getOrgAccessRoleName } from '../../../utils/helpers/orgAccessRequest';
import { initialValues } from './Schema';
import PortalAccessPage from './PortalAccessPage';
import WizardFirstPage from './WizardFirstPage';
import WizardSecondPage from './WizardSecondPage';
import './FormUser.css';

const INFORMATION_STEP = { label: 'com.tempedge.msg.label.userinformation', testId: 'user-step-information' };
const ACCESS_STEP = { label: 'com.tempedge.msg.title.accountconfiguration', testId: 'user-step-access' };
const CONFIRMATION_STEP = { label: 'com.tempedge.msg.label.confirminformation', testId: 'user-step-confirmation' };

const getUserFormSteps = (isEdit) =>
  (isEdit ? [INFORMATION_STEP, ACCESS_STEP, CONFIRMATION_STEP] : [INFORMATION_STEP, CONFIRMATION_STEP]).map((step, index) => ({
    ...step,
    key: index + 1
  }));

const filtersNameRols = ['TE_ADMIN', 'CLIENT', 'CLIENT_PAYROLL', 'CLIENT_FACEREC', 'CLIENT_APPROVER', 'TEMPEDGE_USER'];

export const GENERIC_ERROR_MESSAGE_KEY = 'com.tempedge.error.undefine';

/**
 * Builds the OutcomeBar payload used when a request fails without a usable API message.
 * @returns {Error} Error carrying the generic failure key
 * @author Armando Rico -ai-
 */
const genericError = () => new Error(GENERIC_ERROR_MESSAGE_KEY);

const resolvePortalRole = (roles, portal) => {
  const role = roles.find((item) => item.roleId === portal.userRoleId);
  if (role) return role;
  const name = getOrgAccessRoleName(portal);
  return { roleId: portal.userRoleId, name, description: name };
};

function FormUser({ user }) {
  const [page, setPage] = useState(0);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [data, setData] = useState(initialValues);
  const [valuesFormik, SetValuesFormik] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const username = useSelector((state) => {
    return state.tempEdge.username || '';
  }, shallowEqual);

  useEffect(() => {
    dispatch(resetEntireState());
    async function fetch() {
      if (user) {
        try {
          setLoading(true);
          const { data: roles } = await TempEdgeApi.get({
            url: REACT_APP_URL_ROLE_LIST_ALL
          });
          setData({
            ...initialValues,
            edit: true,
            account: {
              ...initialValues.account,
              firstName: user.user.firstName,
              lastName: user.user.lastName,
              email: user.user.email,
              username: user.user.username,
              password: '',
              passwordConfirmation: '',
              id: user.user.id
            },
            user: {
              orgName: user.orgName,
              agencyoffice: user.officeName || '',
              agencyclient: user.clientName || '',
              id: user.user.id,
              ssnlastfour: user.personIdentification,
              role: roles.result.find((role) => role.roleId === user.userRoleId) || initialValues.user.role
            },
            status: user.status
          });
          setLoading(false);
        } catch (err) {
          setData(initialValues);
          setLoading(false);
        }
      }
    }
    fetch();
  }, [user, dispatch]);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const { data: rol } = await TempEdgeApi.get({
          url: REACT_APP_URL_ROLE_LIST_ALL
        });
        if (rol.status === 200 && rol.code === 'TE00') {
          const filteredData = rol.result.filter((obj) => filtersNameRols.includes(obj.name));
          setRoles(filteredData);
        }
      } catch (e) {
        setMessage(e);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      const path = location.pathname.split('/');
      if (username && path[2] === 'update') {
        try {
          setLoading(true);
          const { data: resp } = await TempEdgeApi.post({
            url: REACT_APP_URL_USER_ID,
            payload: {
              filterBy: {
                username
              }
            }
          });
          if (resp.status === 200 && resp.code === 'TE00') {
            const { data: roles } = await TempEdgeApi.get({
              url: REACT_APP_URL_ROLE_LIST_ALL
            });
            const user = resp.result[0].user;
            setData({
              ...initialValues,
              edit: true,
              account: {
                ...initialValues.account,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                username: user.username,
                password: '******',
                passwordConfirmation: '******'
              },
              user: {
                orgName: resp.result[0].orgName,
                agencyoffice: resp.result[0].officeName || '',
                agencyclient: resp.result[0].clientName || '',
                id: resp.result[0].user.id,
                role: resolvePortalRole(roles.result, resp.result[0])
              },
              portalUserList: resp.result.map((item) => {
                return {
                  portalUserConfId: item.portalUserConfId,
                  orgName: item.orgName,
                  clientId: item.clientId || '',
                  officeId: item.officeId || '',
                  officeName: item.officeName || '',
                  clientName: item.clientName || '',
                  role: resolvePortalRole(roles.result, item),
                  user: item.user.id,
                  personalIdentification: item.personIdentification
                };
              }),
              status: resp.result[0].status
            });
          } else {
            setMessage(resp);
          }
        } catch (e) {
          setMessage(genericError());
        } finally {
          setLoading(false);
        }
      }
    }
    fetch();
  }, [username, location]);

  function next() {
    setPage(page + 1);
  }

  function prev() {
    setPage(page - 1);
  }

  function changeFormikValues(values) {
    SetValuesFormik(values);
    setData(values);
  }
  function resetFormikValues() {
    SetValuesFormik(initialValues);
    setData(initialValues);
    setMessage('');
  }

  function handleSubmit(values) {
    if (values.edit) {
      update(values);
      return;
    }
    save(values);
  }

  async function update(values) {
    let portalList = [];

    if (values.status === 'R') {
      portalList = [
        ...portalList,
        {
          portalUserConfId: values.portalUserList && values.portalUserList.length > 0 ? values.portalUserList[0]?.portalUserConfId : null,
          orgName: values.user.orgName,
          clientName: values.user.agencyclient,
          officeName: values.user.agencyoffice,
          userRoleId: values.user.role.roleId,
          user: {
            ...values.account,
            ...values.user
          },
          personIdentification: values.user.ssnlastfour
        }
      ];
    }

    if (values.status !== 'R')
      portalList = values.portalUserList.map((item) => {
        return {
          portalUserConfId: item.portalUserConfId,
          clientId: item.clientId,
          officeId: item.officeId,
          user: {
            ...values.account,
            id: item.user
          },
          personIdentification: item.personIdentification
        };
      });

    try {
      setLoading(true);
      const { data } = await TempEdgeApi.post({
        url: REACT_APP_URL_USER_UPDATE,
        payload: { portalList }
      });
      setMessage(data);
      setLoading(false);
      setPage(0);
      if (data.status !== 200 && data.code !== 'TE00') {
        setData(values);
        return;
      }
      navigate(-1);
    } catch (err) {
      setPage(0);
      setData(values);
      setMessage(genericError());
      setLoading(false);
    }
  }

  async function save(values) {
    const data = {
      orgName: values.user.orgName,
      user: {
        firstName: values.account.firstName,
        lastName: values.account.lastName,
        username: values.account.username,
        password: values.account.password === values.account.passwordConfirmation ? values.account.password : null,
        email: values.account.email,
        birthday: values.account.birthday,
        gender: values.account.gender
      },
      clientName: values.user.agencyclient || null,
      officeName: values.user.agencyoffice || null,
      roleId: values.user.role.roleId,
      agencyssnlastfour: values.user.ssnlastfour || null,
      personId: values.personId
    };
    try {
      setLoading(true);
      const { data: resp } = await TempEdgeApi.post({
        url: REACT_APP_URL_USER_SAVE,
        payload: data
      });
      setMessage(resp);
      if (resp.status !== 200 && resp.code !== 'TE00') {
        setData(values);
      } else {
        setData(initialValues);
      }
    } catch (err) {
      setData(values);
      setMessage(genericError());
    } finally {
      setLoading(false);
      setPage(0);
    }
  }

  if (loading) return <Loader />;

  const stepper = (
    <div className="user-form-stepper">
      <FormStepper
        testId="user-form-stepper"
        steps={getUserFormSteps(data.edit)}
        activeStep={page + 1}
        onSelect={(key) => key <= page && setPage(key - 1)}
      />
    </div>
  );

  return (
    <div className="wizard-create-agency">
      {page === 0 && (
        <WizardFirstPage
          next={() => next()}
          data={data}
          changeFormikValues={changeFormikValues}
          roles={roles}
          message={message}
          resetFormikValues={resetFormikValues}
          stepper={stepper}
        />
      )}
      {data.edit && page === 1 && (
        <PortalAccessPage valuesFormik={valuesFormik} changeFormikValues={changeFormikValues} prev={prev} next={next} stepper={stepper} />
      )}
      {page === (data.edit ? 2 : 1) && (
        <WizardSecondPage next={() => next()} valuesFormik={valuesFormik} prev={prev} handleSubmit={handleSubmit} stepper={stepper} />
      )}
    </div>
  );
}

export default FormUser;
